import { MutationFunction, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { DocumentType, gql } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { assertPresence } from "utils/assertions";
import { useCurrentUserId } from "utils/currentUserHooks";

const mutation = gql(/* GraphQL */ `
  mutation PatchUserMetadata($userId: UUID!, $metadataPatch: JSON!) {
    patchUserMetadata(
      input: { userId: $userId, metadataPatch: $metadataPatch }
    ) {
      user {
        id
        metadata
      }
    }
  }
`);

type PatchUserMetadataMutationFunction = MutationFunction<
  DocumentType<typeof mutation>,
  DocumentVariablesType<typeof mutation>
>;

type PatchUserMetadata = (
  userId: UUID,
  metadata: GraphQLJSON,
) => ReturnType<PatchUserMetadataMutationFunction>;

type PatchUserMetadataForUser = (
  metadata: GraphQLJSON,
) => ReturnType<PatchUserMetadataMutationFunction>;

export default function usePatchUserMetadata(): PatchUserMetadata {
  const [patchUserMetadata] = useMutation<
    DocumentType<typeof mutation>,
    DocumentVariablesType<typeof mutation>
  >(mutation);

  return useCallback(
    (userId: UUID, metadata: GraphQLJSON) =>
      patchUserMetadata({
        variables: { userId, metadataPatch: metadata },
      }),
    [patchUserMetadata],
  );
}

export function usePatchUserMetadataForUser(
  userId: UUID | null | undefined,
): PatchUserMetadataForUser {
  const patchUserMetadata = usePatchUserMetadata();

  return useCallback(
    (metadata: GraphQLJSON) => {
      assertPresence(userId, "User ID is required to patch user metadata.");
      return patchUserMetadata(userId, metadata);
    },
    [patchUserMetadata, userId],
  );
}

export function usePatchCurrentUserMetadata() {
  const userId = useCurrentUserId();
  return usePatchUserMetadataForUser(userId);
}
